import type {
  Amenity,
  LodgingHomesAmenityKind,
  StayType,
} from "@b2bportal/lodging-api";
import { HotelSortOptionEnum, type TrackingProperties } from "@overrides/types";

export type HotelRoomRateTrackingProperties = {
  properties: {
    refundable: boolean;
    refundable_type: string;
  };
} & { encryptedProperties?: TrackingProperties["encryptedProperties"] };

export const fallbackHotelRoomRateTrackingProperties = {
  properties: {
    refundable: false,
    refundable_type: "",
  },
};

export type HotelRoomTrackingProperties = {
  // Given trackingPropertiesV2 is still not returned by the BFF
  // the selector adds the required properties for room rates related events
  properties: {
    selectable_rates_count: number;
    room_type_name: string;
    room_index_in_shop: number;
    lodging_name: string;
    number_of_photos: number;
    city_name: string;
    country_name: string;
    lodging_id: string;
  };
} & { encryptedProperties?: TrackingProperties["encryptedProperties"] };

export const fallbackHotelRoomInfoTrackingProperties = {
  selectable_rates_count: 0,
  room_type_name: "",
};

export const fallbackHotelRoomTrackingProperties = {
  properties: {
    selectable_rates_count: 0,
    room_type_name: "",
    room_index_in_shop: 0,
    lodging_name: "",
    number_of_photos: 0,
    city_name: "",
    country_name: "",
    lodging_id: "",
  },
};

export type SelectedRoomInfoTrackingProperties = {
  selectable_rates_count: number;
  room_type_name: string;
  number_of_photos: number;
  room_media_shown: boolean;
  room_media_count: number;
};

export type HotelShopTrackingProperties = {
  properties: {
    hopper_preferred_hotel: boolean;
    lodging_list_index: number;
    lodging_id: string;
    city_name: string;
    name: string;
    country: string;
  };
  encryptedProperties?: TrackingProperties["encryptedProperties"];
};

export const fallbackHotelShopTrackingProperties = {
  properties: {
    hopper_preferred_hotel: false,
    lodging_list_index: 0,
    lodging_id: "",
    city_name: "",
    name: "",
    country: "",
  },
};

export type HotelShopAvailabilityTrackingProperties = {
  properties: {
    advance: number;
    check_in_date: string;
    check_out_date: string;
    los: number;
    adults_count: number;
    children_count: number;
    children_ages?: number[];
    room_count: number;
    rooms?: number;
  };
  encryptedProperties?: TrackingProperties["encryptedProperties"];
};

export type HotelSelectedRoomEntityProperties = {
  properties: {
    city_name: string;
    country_name: string;
    lodging_id: string;
    lodging_name: string;
    number_of_photos: number;
    room_index_in_shop: number;
    room_type_name: string;
    selectable_rates_count: number;
  };
  encryptedProperties?: TrackingProperties["encryptedProperties"];
};

export type HotelSelectedRoomRateEntityProperties = {
  properties: {
    refundable: boolean;
    refundable_type: string;
  };
  encryptedProperties?: TrackingProperties["encryptedProperties"];
};

export const fallbackHotelShopAvailabilityTrackingProperties = {
  properties: {
    advance: 0,
    check_in_date: "",
    check_out_date: "",
    los: 0,
    adults_count: 0,
    children_count: 0,
    room_count: 0,
  },
};

export const fallbackHotelAvailabilityTrackingProperties = {
  properties: {
    advance: 0,
    check_in_date: "",
    check_out_date: "",
    los: 0,
    number_of_filters_applied: 0,
    number_of_properties: 0,
    sort_order: HotelSortOptionEnum.Price,
    adults_count: 0,
    children_count: 0,
    room_count: 0,
  },
};

export const fallbackLodgingInListTrackingProperties = {
  properties: {
    breakfast_included_shown: false,
    city_name: "",
    country_name: "",
    daily_owed_overall_excluding_taxes_fees_usd: 0,
    daily_owed_overall_including_taxes_fees_usd: 0,
    im_lodging_name: "",
    is_sponsored_hotel: false,
    lodging_id: "",
    lodging_name: "",
    lodging_type: "",
    market_id: "",
    market_name: "",
    number_of_photos: 0,
    owed_overall_including_taxes_fees_usd: 0,
    page_index: 0,
    provider_name: "",
    rate_provider: "",
    rate_type: "",
  },
};

export type SearchedLocationTrackingProperties = {
  searched_place_id: string;
  searched_place_term: string;
};

export type HotelAvailabilityTrackingProperties = {
  properties: SearchedLocationTrackingProperties & {
    advance: number;
    check_in_date: string;
    check_out_date: string;
    los: number;
    number_of_filters_applied: number;
    number_of_properties: number;
    sort_order: HotelSortOptionEnum;
    adults_count: number;
    children_count: number;
    children_ages?: number[];
    applied_filters_filter_amenities: Amenity[];
    applied_filters_filter_star: number[];
    available_hotels: number;
    unavailable_hotels: number;
    applied_filters_filter_stay_type: StayType[];
    pets_searched: boolean;
    rooms_searched: number;
    room_count: number;
    number_of_guests: number;
    available_vr: number;
    applied_filters_filter_vr_amenities: LodgingHomesAmenityKind[];
    applied_filters_filter_vr_rooms_and_beds: boolean;
  };
  encryptedProperties?: string[];
};

export type HotelViewedLodgingInListTrackingProperties = {
  properties: {
    lodging_list_index?: number;
    breakfast_included_shown: boolean;
    city_name: string;
    country_name: string;
    daily_owed_overall_excluding_taxes_fees_usd: number;
    daily_owed_overall_including_taxes_fees_usd: number;
    im_lodging_name: string;
    is_sponsored_hotel: boolean;
    lodging_id: string;
    lodging_name: string;
    lodging_star_rating?: number;
    lodging_trip_advisor_rating?: number;
    lodging_type: string;
    market_id: string;
    market_name: string;
    number_of_photos: number;
    owed_overall_including_taxes_fees_usd: number;
    page_index: number;
    provider_name: string;
    rate_provider: string;
    rate_type: string;
  };
};

export type HotelShoppedProductGroupedTrackingProperties =
  HotelShopAvailabilityTrackingProperties["properties"] &
    HotelShopTrackingProperties["properties"] &
    HotelRoomTrackingProperties["properties"] &
    HotelRoomRateTrackingProperties["properties"];

export type HotelShoppedProductTrackingProperties = {
  search: HotelShopAvailabilityTrackingProperties;
  hotel: HotelShopTrackingProperties;
  room: HotelRoomTrackingProperties;
  roomRate: HotelRoomRateTrackingProperties;
};

export const fallbackHotelShoppedProductTrackingProperties = {
  search: fallbackHotelShopAvailabilityTrackingProperties,
  hotel: fallbackHotelShopTrackingProperties,
  room: fallbackHotelRoomTrackingProperties,
  roomRate: fallbackHotelRoomRateTrackingProperties,
};
